/**
 * A driven value in words (KPR-950, KPE-010; docs/platform-standards.md §15).
 *
 * A driven element is stored as `options.drivenFrom`: a source card by id, a
 * source element by its stored name (`$createdAt` for the two system dates),
 * an aggregate, and an optional `where` holding a KQL filter with `#id`s and a
 * time window. That is a fine way to STORE the rule, and nobody should have to
 * read it. These are the words the dialog's foot and the Elements tab's
 * summary line say instead: "Counts the Work Item items linked to this Epic
 * whose Status is none of Done, Deployed or Won't do." No `$` name, no `#id`
 * and no query text ever comes out of here: an element reads as its label, a
 * system date as Created or Updated, an item or a tag behind an id as "a
 * chosen item" (the id itself says nothing, and a title may be one the reader
 * cannot see).
 *
 * `drivenFilterProblem` is the dialog's check of a filter a person typed with
 * Edit as text, in words, before the card ever reaches the server: the same
 * refusals the API makes (keepr-api utils/automations/drivenFields.js
 * validateWhere, and the stored-query parse), said where they can be fixed.
 */
import { parse, refNamesOf, usesMe, isHexId, BLOCK_CMP } from './kql';
import {
	buildFilterFields,
	conditionValueLabel,
	entryColumnFields,
	fieldKindOf,
	findField,
	isNullaryOperator,
	isTextMatchOperator,
	operatorLabelFor,
	relativeLabel,
} from './kqlBuilder';
import { formatDisplayDate, formatDisplayDateTime } from './dateDisplay';
import { normalizeId } from './ids';
import { cardAndDescendantIds } from './facets';
import { columnsOf, isRepeatingGroup } from '../config/repeatingGroups';

/** The stored names of the two dates every item has, as a source element. */
export const SYSTEM_SOURCE_LABELS = Object.freeze({ $createdAt: 'Created', $updatedAt: 'Updated' });

/** …and as what a time window measures from. */
export const WINDOW_SYSTEM_LABELS = Object.freeze({ createdAt: 'Created', updatedAt: 'Updated' });

/** The placeholder a count stores for its source element: a count reads no element (the catalog blueprints store the same). */
export const COUNT_SOURCE_ELEMENT = '$createdAt';

/** What reads between a repeating group and one of its columns: "Supplies > Supply". */
export const ENTRY_JOINER = ' > ';

/**
 * A reference into a repeating group's ENTRIES (KPR-1055, keepr-api
 * drivenFields `entryRefOf`): `"<group>.<column>"` → `{ group, column }`,
 * anything else → null. No element name holds a dot, so a dotted
 * `linkElementName`, `sourceElementName` or `orderBy` can only mean a column
 * of a group, and a plain one keeps meaning the element of that name.
 */
export function entryRefOf(name) {
	if (typeof name !== 'string') return null;
	const at = name.indexOf('.');
	if (at <= 0 || at === name.length - 1) return null;
	const group = name.slice(0, at);
	const column = name.slice(at + 1);
	if (column.includes('.')) return null;
	return { group, column };
}

/** An element's (or a column's) label: the singular, a plain string, else its name. */
const labelOf = el => el?.label?.singular || (typeof el?.label === 'string' ? el.label : '') || el?.name || '';

/** The same, for a component that shows an element or a column by its label. */
export const elementLabel = labelOf;

/** The group `<group>` names among a card's elements, or null. */
export const groupNamed = (sourceElements = [], name = '') => (
	(sourceElements || []).filter(el => isRepeatingGroup(el) && el.name === name).pop() || null
);

/** The column a `<group>.<column>` reference names on the source card, `{ group, column }` (each null when it is not there). */
export function entryColumnOf(ref, sourceElements = []) {
	if (!ref) return { group: null, column: null };
	const group = groupNamed(sourceElements, ref.group);
	const column = group ? columnsOf(group).find(c => c?.name === ref.column) || null : null;
	return { group, column };
}

const UNIT_WORDS = Object.freeze({
	minute: ['minute', 'minutes'],
	hour: ['hour', 'hours'],
	day: ['day', 'days'],
	week: ['week', 'weeks'],
});

/** "7 days", "1 week". */
export function windowLengthWords(every, unit) {
	const n = Number(every) || 1;
	const [one, many] = UNIT_WORDS[unit] || UNIT_WORDS.day;
	return `${n} ${n === 1 ? one : many}`;
}

const cardName = card => card?.label?.singular || card?.name || '';

/** "A, B or C" — how a list of picked values reads in a sentence. */
export function joinOr(words) {
	const list = (words || []).filter(word => word !== undefined && word !== null && word !== '');
	if (list.length <= 1) return list.join('');
	return `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}`;
}

// Comparisons on a number or a date read as words; the symbols the builder's
// own menu wears are for a row, not a sentence.
const NUMBER_WORDS = { gt: 'is more than', gte: 'is at least', lt: 'is less than', lte: 'is at most' };
const DATE_WORDS = { gt: 'is after', gte: 'is on or after', lt: 'is before', lte: 'is on or before' };

function operatorWords(field, cmp) {
	const kind = fieldKindOf(field);
	if (kind === 'number' || kind === 'measurement' || kind === 'currency') {
		if (NUMBER_WORDS[cmp]) return NUMBER_WORDS[cmp];
	}
	if (kind === 'date' && DATE_WORDS[cmp]) return DATE_WORDS[cmp];
	return operatorLabelFor(field, cmp);
}

const UNKNOWN_ELEMENT = 'an element that is no longer here';

function fieldLabel(field) {
	if (!field || field.unknown) return UNKNOWN_ELEMENT;
	const label = String(field.label || '');
	// A label is a person's words; a stand-in for a path nobody defined is not
	// (findField's fallback is the path itself, which may hold an id).
	if (!label || /[#$]/.test(label)) return UNKNOWN_ELEMENT;
	return label;
}

/** One value node in words, on its field. */
function valueWords(field, node, fields, cmp) {
	if (!node) return '';
	if (node.type === 'list') return joinOr((node.v || []).map(member => valueWords(field, member, fields, cmp)));
	if (node.type === 'rel') return relativeLabel(node.v).toLowerCase();
	if (node.type === 'me') return 'you';
	if (node.type === 'ref') return `its own ${fieldLabel(findField(fields, [node.v]))}`;
	const raw = node.v;
	const kind = fieldKindOf(field);
	// A tag's name needs the collection's tags; without them it is "a chosen
	// tag", never "an unknown" one (it is known, just not here).
	if (kind === 'tag' && !field?.options?.loaded) return 'a chosen tag';
	// What follows `contains` is text somebody typed, never a stored choice.
	const named = isTextMatchOperator(cmp) && kind === 'choice' ? undefined : conditionValueLabel(field, raw);
	if (named !== undefined && named !== null && named !== '') return String(named);
	if (node.type === 'bool') return raw ? 'yes' : 'no';
	// An id behind a value says nothing to a person, and the title behind it
	// may be one this reader cannot see: it is "a chosen item", never the id.
	if (kind === 'tag') return 'a chosen tag';
	if (kind === 'lookup' || isHexId(String(raw))) return 'a chosen item';
	if (node.type === 'date' || kind === 'date') {
		const text = String(raw);
		const shown = text.includes('T') ? formatDisplayDateTime(text) : formatDisplayDate(text);
		return shown || text;
	}
	if (node.type === 'num') return String(raw);
	return `“${String(raw)}”`;
}

/** A comparison in words: "Status is none of Done, Deployed or Won't do". */
function comparisonWords(node, fields) {
	const field = findField(fields, node.path || [], !!node.forceElement, !!node.facet);
	const label = fieldLabel(field);
	if (node.cmp === BLOCK_CMP) {
		// A repeating group's `has (…)`: the conditions on ONE of its entries,
		// read in the group's own columns.
		const inner = node.value?.v || null;
		const columns = entryColumnFields(field);
		return `${label} has an entry where ${nodeWords(inner, columns, true)}`;
	}
	if (isNullaryOperator(node.cmp)) return `${label} ${operatorWords(field, node.cmp)}`;
	return `${label} ${operatorWords(field, node.cmp)} ${valueWords(field, node.value, fields, node.cmp)}`;
}

/** Any node in words; a group inside another reads in brackets. */
function nodeWords(node, fields, top = false) {
	if (!node) return '';
	if (node.op === 'cmp') return comparisonWords(node, fields);
	if (node.op === 'not') return `not (${nodeWords(node.arg, fields, true)})`;
	if (node.op === 'and' || node.op === 'or') {
		const text = (node.args || []).map(arg => nodeWords(arg, fields)).join(node.op === 'and' ? ' and ' : ' or ');
		return top ? text : `(${text})`;
	}
	return '';
}

/**
 * A filter's text as one plain sentence over `fields` (a filter vocabulary:
 * kqlBuilder `buildFilterFields` of the source card and the cards beneath it).
 * '' for no filter, null when the text does not read.
 */
export function conditionSentence(text, fields = []) {
	const trimmed = String(text || '').trim();
	if (!trimmed) return '';
	let ast;
	try {
		ast = parse(trimmed);
	} catch (err) {
		return null;
	}
	return nodeWords(ast, fields, true);
}

/** Does this sentence start with a plain condition ("whose …" reads), or with "not" / a group ("where …")? */
const readsWithWhose = (text) => {
	try {
		const ast = parse(String(text || '').trim());
		return ast.op === 'cmp' || (ast.op === 'and' && (ast.args || []).every(arg => arg.op === 'cmp'));
	} catch (err) {
		return false;
	}
};

/** The source element's label, or what a system date reads as. */
export function sourceElementLabel(name, sourceElements = []) {
	if (!name) return '';
	if (SYSTEM_SOURCE_LABELS[name]) return SYSTEM_SOURCE_LABELS[name];
	const ref = entryRefOf(name);
	if (ref) {
		// A column of a repeating group reads "Group > Column".
		const { group, column } = entryColumnOf(ref, sourceElements);
		return group && column ? `${labelOf(group)}${ENTRY_JOINER}${labelOf(column)}` : UNKNOWN_ELEMENT;
	}
	const element = (sourceElements || []).find(el => el?.name === name);
	return element ? (element.label?.singular || element.name) : UNKNOWN_ELEMENT;
}

/**
 * The same, for a surface that shows the stored name when the card is not to
 * hand: a plain name falls back to itself (as those surfaces always did), a
 * dotted one to its parts ("supplies > quantity"), never the raw `a.b`.
 */
export function drivenSourceLabel(name, sourceElements = []) {
	const label = sourceElementLabel(name, sourceElements);
	if (label !== UNKNOWN_ELEMENT) return label;
	const ref = entryRefOf(name);
	return ref ? `${ref.group}${ENTRY_JOINER}${ref.column}` : name;
}

/**
 * Which date orders a `latest` (keepr-api drivenFields, the default the API
 * derives and the dialog leaves unsaved): the config's own `orderBy`, else the
 * source itself when it is a date, a date-time or a `$` date, else creation
 * time. A column of a group is read from the group, so a source that is a date
 * COLUMN orders by that column (`supplies.date`).
 */
export function effectiveOrderBy(config, sourceElements = []) {
	if (config?.orderBy) return config.orderBy;
	const name = config?.sourceElementName;
	if (!name) return '$createdAt';
	if (SYSTEM_SOURCE_LABELS[name]) return name;
	const ref = entryRefOf(name);
	const source = ref
		? entryColumnOf(ref, sourceElements).column
		: (sourceElements || []).find(el => el?.name === name);
	return source && (source.dataType === 'date' || source.dataType === 'date-time') ? name : '$createdAt';
}

/** What a window measures from, in words. */
export function windowFieldLabel(field, sourceElements = []) {
	if (!field) return '';
	if (WINDOW_SYSTEM_LABELS[field]) return WINDOW_SYSTEM_LABELS[field];
	const element = (sourceElements || []).find(el => el?.name === field);
	return element ? (element.label?.singular || element.name) : UNKNOWN_ELEMENT;
}

/**
 * The whole rule as one sentence. `config` is `options.drivenFrom` (or the
 * dialog's unsaved draft of it); the rest is what to read it with:
 *   sourceCard, targetCard  the card objects (names)
 *   sourceElements          the source card's merged elements
 *   fields                  the filter vocabulary (see conditionSentence)
 *   linkLabel               the label of the lookup that links them, when
 *                           more than one could (said only then)
 * '' when there is no source card yet.
 */
export function describeDrivenRule(config, { sourceCard = null, targetCard = null, sourceElements = [], fields = [], linkLabel = '' } = {}) {
	if (!config || !config.sourceCardId) return '';
	const source = cardName(sourceCard) || 'another card';
	const target = cardName(targetCard) || 'card';
	const entryLink = entryRefOf(config.linkElementName);

	let sentence;
	if (entryLink) {
		// A total read across a repeating group's ENTRIES (KPR-1055): the
		// entries that name this item are what is counted, and a condition
		// below picks which RECORDS count, never which entries.
		const { group, column: linkColumn } = entryColumnOf(entryLink, sourceElements);
		const groupName = group ? labelOf(group) : 'a repeating group that is no longer there';
		const entries = `entries of ${groupName} on ${source} items that name this ${target}${linkLabel && linkColumn && labelOf(linkColumn).toLowerCase() !== target.toLowerCase() ? ` in the ${labelOf(linkColumn)} column` : ''}`;
		const sourceRef = entryRefOf(config.sourceElementName);
		let element;
		if (sourceRef && sourceRef.group === entryLink.group) {
			const { column } = entryColumnOf(sourceRef, sourceElements);
			element = column ? labelOf(column) : UNKNOWN_ELEMENT;
		} else {
			element = sourceElementLabel(config.sourceElementName, sourceElements) || 'an element';
		}
		switch (config.aggregate) {
			case 'count': sentence = `Counts the ${entries}`; break;
			case 'sum': sentence = `Adds up ${element} over the ${entries}`; break;
			case 'average': sentence = `Averages ${element} over the ${entries}`; break;
			case 'min': sentence = `Takes the smallest ${element} of the ${entries}`; break;
			case 'max': sentence = `Takes the largest ${element} of the ${entries}`; break;
			default: sentence = `Takes ${element} from the latest of the ${entries}`;
		}
		const filter = String(config.where?.filter || '').trim();
		if (filter) {
			const words = conditionSentence(filter, fields);
			sentence += words === null
				? ', with a condition that can’t be read'
				: `, counting only ${source} items ${readsWithWhose(filter) ? 'whose' : 'where'} ${words}`;
		}
		const window = config.where?.window;
		if (window && window.field) {
			sentence += `${filter ? ',' : ''} from the last ${windowLengthWords(window.every, window.unit)} by ${windowFieldLabel(window.field, sourceElements)}`;
		}
		return `${sentence}.`;
	}

	const linked = `linked to this ${target}${linkLabel && linkLabel.toLowerCase() !== target.toLowerCase() ? ` by ${linkLabel}` : ''}`;
	const element = sourceElementLabel(config.sourceElementName, sourceElements) || 'an element';

	switch (config.aggregate) {
		case 'count': sentence = `Counts the ${source} items ${linked}`; break;
		case 'sum': sentence = `Adds up ${element} on the ${source} items ${linked}`; break;
		case 'average': sentence = `Averages ${element} over the ${source} items ${linked}`; break;
		case 'min': sentence = `Takes the smallest ${element} of the ${source} items ${linked}`; break;
		case 'max': sentence = `Takes the largest ${element} of the ${source} items ${linked}`; break;
		default: sentence = `Takes ${element} from the latest ${source} item ${linked}`;
	}

	const filter = String(config.where?.filter || '').trim();
	if (filter) {
		const words = conditionSentence(filter, fields);
		sentence += words === null
			? ', with a condition that can’t be read'
			: ` ${readsWithWhose(filter) ? 'whose' : 'where'} ${words}`;
	}
	const window = config.where?.window;
	if (window && window.field) {
		sentence += `${filter ? ',' : ''} from the last ${windowLengthWords(window.every, window.unit)} by ${windowFieldLabel(window.field, sourceElements)}`;
	}
	return `${sentence}.`;
}

/**
 * The target card and every ancestor of it, as ids: an item lookup pointing at
 * any of them links a source item to THIS card's items (grants and lookups
 * both include descendants).
 */
export function targetCardChain(targetCard, cards = []) {
	const byId = new Map((cards || []).map(card => [normalizeId(card?._id), card]));
	const ids = new Set();
	const selfId = normalizeId(targetCard?._id);
	if (selfId) ids.add(selfId);
	let cursor = normalizeId(targetCard?.parentCardId);
	const guard = new Set();
	while (cursor && !guard.has(cursor)) {
		guard.add(cursor);
		ids.add(cursor);
		cursor = normalizeId(byId.get(cursor)?.parentCardId);
	}
	return ids;
}

/**
 * The source card's lookups that point at the target (or an ancestor):
 * `[{ value, label }]`. Beside the card's own lookups, each repeating group's
 * card-lookup COLUMNS aimed at the target are offered, written as the API
 * stores them (`<group>.<column>`, KPR-1055) and read "Group > Column": a
 * total over the entries that name this item.
 */
export function linkOptionsFor(sourceElements = [], chain = new Set()) {
	const own = (sourceElements || [])
		.filter(el => el?.dataType === 'card-lookup' && chain.has(normalizeId(el?.options?.lookupCardId)))
		.map(el => ({ value: el.name, label: el?.label?.singular || el.name }));
	const seen = new Set();
	const entries = [];
	(sourceElements || []).forEach((el) => {
		// A re-declared group name is read once, as the engine reads it: the last definition.
		if (!isRepeatingGroup(el) || !el.name || seen.has(el.name)) return;
		seen.add(el.name);
		const group = groupNamed(sourceElements, el.name);
		columnsOf(group)
			.filter(column => column?.name && column.dataType === 'card-lookup' && chain.has(normalizeId(column?.options?.lookupCardId)))
			.forEach(column => entries.push({ value: `${group.name}.${column.name}`, label: `${labelOf(group)}${ENTRY_JOINER}${labelOf(column)}` }));
	});
	return [...own, ...entries];
}

/** The link's label, said only when more than one lookup could be it. */
export function linkLabelFor(linkOptions = [], linkElementName = '') {
	if ((linkOptions || []).length < 2) return '';
	return linkOptions.find(option => option.value === linkElementName)?.label || '';
}

/**
 * A STORED driven value in words, from what a card editor has to hand: the
 * collection's cards, the target card, and a resolver for a card's merged
 * elements (CardDefinitionsContext's `getElementsForCard`). The Elements tab's
 * summary line — the same sentence the dialog shows at its foot.
 */
export function describeStoredDrivenRule(config, { cards = [], targetCard = null, getElementsForCard = null } = {}) {
	const sourceCardId = normalizeId(config?.sourceCardId);
	if (!sourceCardId) return '';
	const sourceCard = (cards || []).find(card => normalizeId(card?._id) === sourceCardId) || null;
	const elementsOf = id => (typeof getElementsForCard === 'function'
		? (getElementsForCard(id) || [])
		: ((cards || []).find(card => normalizeId(card?._id) === id)?.elements || []));
	const sourceElements = elementsOf(sourceCardId);
	const below = cardAndDescendantIds(cards, sourceCardId).filter(id => id !== sourceCardId);
	const fields = buildFilterFields({
		cards: [
			{ _id: sourceCardId, elements: sourceElements },
			...below.map(id => ({ _id: id, elements: elementsOf(id) })),
		],
	});
	const links = linkOptionsFor(sourceElements, targetCardChain(targetCard, cards));
	return describeDrivenRule(config, {
		sourceCard,
		targetCard,
		sourceElements,
		fields,
		linkLabel: linkLabelFor(links, config?.linkElementName),
	});
}

const SEARCH_PATH = 'text';

const searches = (node) => {
	if (!node) return false;
	if (node.op === 'not') return searches(node.arg);
	if (node.op === 'and' || node.op === 'or') return (node.args || []).some(searches);
	return node.op === 'cmp' && !node.forceElement && Array.isArray(node.path) && node.path.length === 1 && node.path[0] === SEARCH_PATH;
};

/** Why a driven value's condition can't name the caller. */
export const DRIVEN_ME_PROBLEM = 'A condition here can’t ask about “me”: a driven value is the same for everyone who reads it.';

/**
 * What is wrong with a filter typed as text, in words, or null. The API's own
 * refusals for a driven value's `where.filter`: it must read; it cannot use
 * another item's values (there is no item to read them from); it cannot ask
 * about "me" (a total is one value for everyone); it cannot search.
 */
export function drivenFilterProblem(text) {
	const trimmed = String(text || '').trim();
	if (!trimmed) return null;
	if (trimmed.includes('{{')) return 'A condition here can’t use another element’s value: it chooses which linked items count, and there is no item to read the value from.';
	let ast;
	try {
		ast = parse(trimmed);
	} catch (err) {
		const reason = String(err?.message || '').trim();
		return `This condition can’t be read${reason ? `: ${reason}` : '.'}`;
	}
	if (refNamesOf(ast).length) return 'A condition here can’t use another element’s value: it chooses which linked items count, and there is no item to read the value from.';
	if (usesMe(ast)) return DRIVEN_ME_PROBLEM;
	if (searches(ast)) return 'A condition here can’t search all of an item’s text. Pick an element to compare instead.';
	return null;
}
